import React, { useEffect } from 'react';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useLeadFormStore } from '../../../store/leadFormStore';
import { ChildFormData } from '../../../types/leadForm.types';
import { StepProps } from '../types';
import { useProjectSegments } from '../../../hooks/useProjectSegments';
import { SchoolSegmentSelect } from '../SchoolSegmentSelect';

const GRADE_OPTIONS = [
  { value: '', label: 'Select Grade' },
  { value: 'Preschool', label: 'Preschool' },
  { value: 'Pre-K', label: 'Pre-K' },
  { value: 'K', label: 'Kindergarten' },
  { value: '1', label: '1st Grade' },
  { value: '2', label: '2nd Grade' },
  { value: '3', label: '3rd Grade' },
  { value: '4', label: '4th Grade' },
  { value: '5', label: '5th Grade' },
  { value: '6', label: '6th Grade' },
  { value: '7', label: '7th Grade' },
  { value: '8', label: '8th Grade' },
  { value: '9', label: '9th Grade' },
  { value: '10', label: '10th Grade' },
  { value: '11', label: '11th Grade' },
  { value: '12', label: '12th Grade' }
];

const ChildrenStep: React.FC<StepProps> = ({ onNext, onBack }) => {
  const {
    formData,
    projectId,
    updateFormData,
    setChildrenData,
    addChild,
    removeChild,
    errors,
    setError,
    clearError,
    markStepCompleted,
    saveToStorage
  } = useLeadFormStore();

  const { children } = formData;
  const { segments } = useProjectSegments(projectId);

  // Calculate age from date of birth
  const calculateAge = (dateOfBirth: string): number => {
    if (!dateOfBirth) return 0;
    
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    return Math.max(0, age);
  };

  // Validate child data
  const validateChild = (child: ChildFormData, index: number): boolean => {
    let isValid = true;

    // First name validation
    if (!child.firstName.trim()) {
      setError(`child_${index}_firstName`, 'First name is required');
      isValid = false;
    } else {
      clearError(`child_${index}_firstName`);
    }

    // Last name validation
    if (!child.lastName.trim()) {
      setError(`child_${index}_lastName`, 'Last name is required');
      isValid = false;
    } else {
      clearError(`child_${index}_lastName`);
    }

    // Age validation
    if (child.age <= 0 || child.age > 25) {
      setError(`child_${index}_age`, 'Please enter a valid age');
      isValid = false;
    } else {
      clearError(`child_${index}_age`);
    }

    // Grade validation
    if (!child.grade.trim()) {
      setError(`child_${index}_grade`, 'Grade is required');
      isValid = false;
    } else {
      clearError(`child_${index}_grade`);
    }

    return isValid;
  };

  // Update child data
  const updateChild = (index: number, field: keyof ChildFormData, value: string | number) => {
    const updatedChildren = children.map((child, i) => {
      if (i === index) {
        const updatedChild = { ...child, [field]: value };
        
        // Auto-calculate age when date of birth changes
        if (field === 'dateOfBirth' && typeof value === 'string') {
          updatedChild.age = calculateAge(value);
        }
        
        return updatedChild;
      }
      return child;
    });

    setChildrenData(updatedChildren);
    saveToStorage();

    // Validate the updated child
    setTimeout(() => validateChild(updatedChildren[index], index), 0);
  };

  // Add new child
  const handleAddChild = () => {
    const newChild: ChildFormData = {
      firstName: '',
      lastName: '',
      age: 0,
      grade: '',
      dateOfBirth: '',
      currentSchool: ''
    };
    
    addChild(newChild);
    saveToStorage();
  };

  // Remove child
  const handleRemoveChild = (index: number) => {
    // Clear any errors for this child
    Object.keys(errors).forEach(key => {
      if (key.startsWith(`child_${index}_`)) {
        clearError(key);
      }
    });
    
    removeChild(index);
    saveToStorage();
  };

  // Check if step is completed
  const checkStepCompletion = () => {
    if (children.length === 0) return false;
    
    const allValid = children.every((child, index) => validateChild(child, index));
    
    if (allValid) {
      markStepCompleted('children');
    }
    
    return allValid;
  };

  // Check completion when children change
  useEffect(() => {
    if (children.length > 0) {
      checkStepCompletion();
    }
  }, [children.length]);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-medium text-gray-900">Children Information</h2>
        <p className="text-sm text-gray-600 mt-1">
          Add details for each child who will be enrolled
        </p>
      </div>

      {segments.length > 0 && (
        <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
          <SchoolSegmentSelect
            segments={segments}
            value={formData.schoolSegmentId}
            onChange={(id) => { updateFormData({ schoolSegmentId: id }); saveToStorage(); }}
            label="School / Campus (for this family) *"
            emptyLabel="Select a school…"
          />
          <p className="text-xs text-gray-500 mt-1">
            Required. Applies to all children below unless a child is given a different campus.
          </p>
        </div>
      )}

      {children.length > 0 && (
        <div className="space-y-6">
          {children.map((child, index) => (
            <div key={index} className="border border-gray-200 rounded-lg p-6 relative">
              {/* Remove button */}
              <button
                type="button"
                onClick={() => handleRemoveChild(index)}
                className="absolute top-4 right-4 text-gray-400 hover:text-red-600 transition-colors"
              >
                <TrashIcon className="h-5 w-5" />
              </button>

              <div className="mb-4">
                <h3 className="text-md font-medium text-gray-900">
                  Child {index + 1}
                </h3>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {/* First Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    First Name *
                  </label>
                  <input
                    type="text"
                    value={child.firstName}
                    onChange={(e) => updateChild(index, 'firstName', e.target.value)}
                    className={`mt-1 block w-full rounded-md shadow-sm ${
                      errors[`child_${index}_firstName`]
                        ? 'border-red-300 focus:ring-red-500 focus:border-red-500' 
                        : 'border-gray-300 focus:ring-primary-500 focus:border-primary-500'
                    }`}
                    placeholder="Child's first name"
                  />
                  {errors[`child_${index}_firstName`] && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors[`child_${index}_firstName`]}
                    </p>
                  )}
                </div>

                {/* Last Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    value={child.lastName}
                    onChange={(e) => updateChild(index, 'lastName', e.target.value)}
                    className={`mt-1 block w-full rounded-md shadow-sm ${
                      errors[`child_${index}_lastName`]
                        ? 'border-red-300 focus:ring-red-500 focus:border-red-500' 
                        : 'border-gray-300 focus:ring-primary-500 focus:border-primary-500'
                    }`}
                    placeholder="Child's last name"
                  />
                  {errors[`child_${index}_lastName`] && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors[`child_${index}_lastName`]}
                    </p>
                  )}
                </div>

                {/* Date of Birth */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Date of Birth
                  </label>
                  <input
                    type="date"
                    value={child.dateOfBirth}
                    onChange={(e) => updateChild(index, 'dateOfBirth', e.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                {/* Age */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Age *
                  </label>
                  <input
                    type="number"
                    value={child.age || ''}
                    onChange={(e) => updateChild(index, 'age', parseInt(e.target.value) || 0)}
                    className={`mt-1 block w-full rounded-md shadow-sm ${
                      errors[`child_${index}_age`]
                        ? 'border-red-300 focus:ring-red-500 focus:border-red-500' 
                        : 'border-gray-300 focus:ring-primary-500 focus:border-primary-500'
                    }`}
                    placeholder="Age"
                    min="0"
                    max="25"
                  />
                  {errors[`child_${index}_age`] && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors[`child_${index}_age`]}
                    </p>
                  )}
                </div>

                {/* Grade */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Grade *
                  </label>
                  <select
                    value={child.grade}
                    onChange={(e) => updateChild(index, 'grade', e.target.value)}
                    className={`mt-1 block w-full rounded-md shadow-sm ${
                      errors[`child_${index}_grade`]
                        ? 'border-red-300 focus:ring-red-500 focus:border-red-500' 
                        : 'border-gray-300 focus:ring-primary-500 focus:border-primary-500'
                    }`}
                  >
                    {GRADE_OPTIONS.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  {errors[`child_${index}_grade`] && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors[`child_${index}_grade`]}
                    </p>
                  )}
                </div>

                {/* Current School */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Current School
                  </label>
                  <input
                    type="text"
                    value={child.currentSchool}
                    onChange={(e) => updateChild(index, 'currentSchool', e.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Current school name"
                  />
                </div>

                {/* Per-child campus override */}
                {segments.length > 0 && (
                  <SchoolSegmentSelect
                    segments={segments}
                    value={child.schoolSegmentId}
                    onChange={(id) => updateChild(index, 'schoolSegmentId', id || '')}
                    label="Campus (this child)"
                    emptyLabel="Same as family"
                  />
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Child Button */}
      <div className="text-center">
        <button
          type="button"
          onClick={handleAddChild}
          className="inline-flex items-center px-4 py-2 border border-dashed border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:border-gray-400 hover:text-gray-800 transition-colors"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Add Child
        </button>
      </div>

      {children.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          <p className="text-sm">No children added yet</p>
          <p className="text-xs mt-1">Complete information for at least one child to proceed</p>
        </div>
      )}

      {/* Progress indicator */}
      <div className="mt-6 p-4 bg-blue-50 rounded-md">
        <h3 className="text-sm font-medium text-blue-800 mb-2">Step 2.4 Status:</h3>
        <div className="text-xs text-blue-600">
          ✅ Children form with validation working
        </div>
        <div className="text-xs text-blue-500 mt-1">
          Complete information for at least one child to proceed
        </div>
      </div>
    </div>
  );
};

export default ChildrenStep;
